import React, { useImperativeHandle, useRef } from 'react'
import {
	ConnectDropTarget,
	ConnectDragSource,
	DropTargetMonitor,
	DragSourceMonitor,
	DragSource,
	DropTarget,
	DropTargetConnector,
	DragSourceConnector,
} from 'react-dnd'
import { Image as ImagePb } from '../../rpc/admin/image_pb';
import { getInstance, Instance } from '../../helpers/instance';
import feather from '../../img/feather-sprite.svg';

export interface ImageProps {
	id: any;
	image: ImagePb;
    index: number;
    disabled: boolean;
	moveImage: (dragIndex: number, hoverIndex: number) => void;
	deleteImage: (index: number) => void;

	isDragging: boolean;
	connectDragSource: ConnectDragSource;
	connectDropTarget: ConnectDropTarget;
}

interface ImageInstance {
	getNode(): HTMLDivElement | null;
}

const thumbnailUrl = (path: string): string => {
    path = path.replace('/originals/', '/128x128/');
    if (getInstance() === Instance.InstanceLocal) {
        return 'https://dashboard.hawkamerican.dev/' + path;
    }
    return '/' + path;
}

const Image = React.forwardRef<HTMLDivElement, ImageProps>(
	({ image, isDragging, connectDragSource, connectDropTarget, index, disabled, deleteImage }, ref) => {
		const elementRef = useRef(null);
		connectDragSource(elementRef);
		connectDropTarget(elementRef);

		const imageClass = isDragging ? 'image-dragging' : '';
		useImperativeHandle<{}, ImageInstance>(ref, () => ({
			getNode: () => elementRef.current,
		}))
		return (
            <div ref={elementRef} className="image-wrapper">
                <img src={thumbnailUrl(image.getPath())} alt={image.getPath()} className={imageClass} width="128" height="128"/>
                <button className="button is-small" title="Delete" disabled={disabled} onClick={() => deleteImage(index)}>
                    <svg width="16" height="16" className="button-icon">
                        <use href={feather + "#trash"}/>
                    </svg>
                </button>
            </div>
		)
	},
)

export default DropTarget(
	'image',
	{
		hover(
			props: ImageProps,
			monitor: DropTargetMonitor,
			component: ImageInstance,
		) {
			if (!component) {
				return null;
			}
			// node = HTML Div element from imperative API
			const node = component.getNode();
			if (!node) {
				return null;
			}

			const dragIndex = monitor.getItem().index;
			const hoverIndex = props.index;

			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Time to actually perform the action
			props.moveImage(dragIndex, hoverIndex);

			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			monitor.getItem().index = hoverIndex;
		},
	},
	(connect: DropTargetConnector) => ({
		connectDropTarget: connect.dropTarget(),
	}),
)(
	DragSource(
		'image',
		{
			beginDrag: (props: ImageProps) => ({
				id: props.id,
				index: props.index,
			}),
		},
		(connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
			connectDragSource: connect.dragSource(),
			isDragging: monitor.isDragging(),
		}),
	)(Image),
)